'use client'

import { BUTTON_VARIANT } from '@fukku/button/Button.types'
import { ButtonContentLink } from '@fukku/button/ContentLink/ButtonContentLink'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { dataTestIds } from 'pdp/src/constants/dataTestIds/dataTestIds'
import { useAnalytics } from 'pdp/src/contexts/AnalyticsProvider/AnalyticsProvider'
import type { CommonProps } from 'pdp/src/types/CommonProps'
import { PdpActionEvents } from 'pdp/src/types/PdpActionEvents'
import { usePdpFeatureFlags } from 'pdp/src/utils/getFeatureFlags/client/usePdpFeatureFlags'
import { env } from 'utils/envManager'

import styles from './Share.module.css'

type ShareProps = Pick<CommonProps, 'product' | 'color'> & {
	readonly className?: string
}

const UTM_SOURCE = {
	KEY: 'utm_source',
	VALUE: 'product-share',
}

const UTM_MEDIUM = {
	KEY: 'utm_medium',
	VALUE: 'social',
}

export function Share({
	product,
	color: { id: colorId },
	className,
}: ShareProps): React.ReactNode {
	const { t } = useLabels()
	const { url, name } = product
	const { sendPdpActionEvent } = useAnalytics()
	const { share: shareFeatureFlag } = usePdpFeatureFlags()

	if (!shareFeatureFlag) {
		return null
	}

	const onShareClick = async (): Promise<void> => {
		if (url) {
			const shareUrl = new URL(url, env.NEXT_PUBLIC_SITE_BASE_URL)

			const queryParams = new URLSearchParams({
				[UTM_SOURCE.KEY]: UTM_SOURCE.VALUE,
				[UTM_MEDIUM.KEY]: UTM_MEDIUM.VALUE,
				c: colorId,
			})

			shareUrl.search = queryParams.toString()

			sendPdpActionEvent(PdpActionEvents.SHARE)

			try {
				await navigator.share({
					text: name,
					url: shareUrl.href,
				})
			} catch {
				// Continue regardless of error
			}
		}
	}

	return (
		url && (
			<div className={className}>
				<ButtonContentLink
					variant={BUTTON_VARIANT.DEFAULT}
					onClick={onShareClick}
					data-testid={dataTestIds['pdp.productInfo.share']}
					className={styles.shareButton}
				>
					{t('plp.pdp.share.button')}
				</ButtonContentLink>
			</div>
		)
	)
}
